import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import { useArchiveOrder } from './useArchiveOrder';
import { useUpdateOrderDetails } from './useUpdateOrderDetails';
import { useCompleteOrder } from './useCompleteOrder';
import { getBulkActionSuccessTitle, OrderBulkActionId } from '../utils/orderBulkActions';

export const useOrderBulkActions = (onSuccess?: () => void) => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const archiveOrder = useArchiveOrder();
    const updateOrderDetails = useUpdateOrderDetails();
    const completeOrder = useCompleteOrder();

    const isLoading =
        archiveOrder.isPending ||
        updateOrderDetails.isPending ||
        completeOrder.isPending;

    const invalidateFeeds = useCallback(async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['/orders/home-feed-paginated'] }),
            queryClient.invalidateQueries({ queryKey: ['/dashboard/orders-feed-paginated'] }),
        ]);
    }, [queryClient]);

    const runBulkAction = useCallback(async (actionId: OrderBulkActionId, orderIds: string[]) => {
        if (orderIds.length === 0) return;

        try {
            // The bulk archive endpoint is best-effort: it reports how many of the
            // requested orders actually matched (existed in the org) vs were skipped
            // because they no longer exist. `complete`/`flag`/`review` don't return
            // these counts (complete is all-or-none and 400s on any bad apple).
            let archiveCounts: { matchedCount?: number; requestedCount?: number } | undefined;

            switch (actionId) {
                case 'archive': {
                    const res = await archiveOrder.mutateAsync({ orderIds, archived: true });
                    archiveCounts = res.data as typeof archiveCounts;
                    break;
                }
                case 'unarchive': {
                    const res = await archiveOrder.mutateAsync({ orderIds, archived: false });
                    archiveCounts = res.data as typeof archiveCounts;
                    break;
                }
                case 'flag':
                    await updateOrderDetails.mutateAsync({ orderIds, manuallyFlagged: true });
                    break;
                case 'unflag':
                    await updateOrderDetails.mutateAsync({ orderIds, manuallyFlagged: false });
                    break;
                case 'mark-reviewed':
                    await updateOrderDetails.mutateAsync({ orderIds, reviewedByMe: true });
                    break;
                case 'remove-review':
                    await updateOrderDetails.mutateAsync({ orderIds, reviewedByMe: false });
                    break;
                case 'complete':
                    await completeOrder.mutateAsync({ orderIds });
                    break;
            }

            await invalidateFeeds();
            onSuccess?.();

            if (actionId === 'archive' || actionId === 'unarchive') {
                const requested = archiveCounts?.requestedCount ?? orderIds.length;
                const matched = archiveCounts?.matchedCount ?? requested;
                const missing = Math.max(requested - matched, 0);

                if (missing > 0) {
                    const noun = requested === 1 ? 'Order' : 'Orders';
                    toast({
                        title: `${matched} of ${requested} ${noun} ${actionId === 'archive' ? 'Archived' : 'Unarchived'}`,
                        description: `${missing} order${missing === 1 ? '' : 's'} couldn't be found and ${missing === 1 ? 'was' : 'were'} skipped.`,
                        status: 'warning',
                        duration: 4000,
                    });
                } else {
                    toast({
                        title: getBulkActionSuccessTitle(actionId, matched),
                        status: 'success',
                        duration: 2500,
                    });
                }
            } else {
                toast({
                    title: getBulkActionSuccessTitle(actionId, orderIds.length),
                    status: 'success',
                    duration: 2500,
                });
            }
        } catch {
            toast({
                title: 'Bulk action failed',
                status: 'error',
                duration: 3000,
            });
        }
    }, [
        archiveOrder,
        completeOrder,
        invalidateFeeds,
        onSuccess,
        toast,
        updateOrderDetails,
    ]);

    return { runBulkAction, isLoading };
};
